import React from 'react';
import { API_BASE_URL } from '../config.js';

/**
 * DocumentRenderer – Production-grade ID Card renderer.
 * Dynamically binds real student, staff, and institution branding data.
 */
const DocumentRenderer = ({
  documentType = 'ID_CARD',
  templateName = 'Classic Academic',
  primaryColor = '#1e3a8a',
  secondaryColor = '#f59e0b',
  logoUrl = '',
  signatureUrl = '',
  sealUrl = '',
  institutionName = '',
  institutionAddress = '',
  footerText = 'If found, please return to the administration office.',
  academicYear = '',
  data = {},
  side = 'front'
}) => {
  const {
    name = '',
    photoUrl = '',
    id = '',
    role = 'STUDENT',
    department = '',
    designation = 'Student',
    validUntil = '',
    emergencyContact = '',
    address = '',
    verificationToken = '',
    fatherName = '',
    bloodGroup = '',
    dob = '',
    phone = '',
    course = '',
    className = '',
    section = '',
    isSchool = true
  } = data;

  const BASE = API_BASE_URL;
  const defaultLogo = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><circle cx="40" cy="40" r="38" fill="' + primaryColor + '" stroke="' + secondaryColor + '" stroke-width="3"/><text x="40" y="48" text-anchor="middle" font-size="28" font-weight="bold" fill="white" font-family="serif">S</text></svg>');
  
  const formatAssetUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) return url;
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return `${BASE}${cleanUrl}`;
  };

  const displayLogo = logoUrl ? formatAssetUrl(logoUrl) : defaultLogo;
  const displaySignature = signatureUrl ? formatAssetUrl(signatureUrl) : '';
  const displaySeal = sealUrl ? formatAssetUrl(sealUrl) : '';

  const hasPhoto = Boolean(photoUrl && photoUrl !== 'null' && photoUrl !== 'undefined');
  const displayPhoto = hasPhoto ? formatAssetUrl(photoUrl) : '';

  const storedOrg = (() => {
    try {
      const item = localStorage.getItem('orgData');
      return item ? JSON.parse(item) : null;
    } catch(e) { return null; }
  })();
  const trustName = data?.trustName || storedOrg?.trustName || localStorage.getItem('trustName') || '';
  const orgName = institutionName || storedOrg?.orgName || storedOrg?.institutionName || storedOrg?.name || 'Institution Name';
  const orgAddress = institutionAddress || storedOrg?.address || '';
  const sessionYear = academicYear || storedOrg?.academicYear || '2026-27';
  const orgPhone = data?.orgPhone || 
    data?.adminPhone ||
    storedOrg?.phoneNumber || 
    storedOrg?.phone || 
    storedOrg?.contactPhone || 
    storedOrg?.adminPhone ||
    localStorage.getItem('adminPhone') || 
    localStorage.getItem('phoneNumber') || 
    localStorage.getItem('phone') || 
    '';
  const orgEmail = data?.orgEmail || 
    data?.adminEmail ||
    storedOrg?.email || 
    storedOrg?.contactEmail || 
    localStorage.getItem('email') || 
    '';

  // Format Date of Birth to display Day first, then Month, then Year (DD-MM-YYYY)
  const formatDob = (dateStr) => {
    if (!dateStr || dateStr === 'N/A' || dateStr === '—') return '';
    try {
      const cleanStr = String(dateStr).trim().split('T')[0];
      // Match YYYY-MM-DD or YYYY/MM/DD
      const ymd = cleanStr.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
      if (ymd) {
        const year = ymd[1];
        const month = ymd[2].padStart(2, '0');
        const day = ymd[3].padStart(2, '0');
        return `${day}-${month}-${year}`;
      }
      // Match DD-MM-YYYY or DD/MM/YYYY (already in date-first format)
      const dmy = cleanStr.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
      if (dmy) {
        const day = dmy[1].padStart(2, '0');
        const month = dmy[2].padStart(2, '0');
        const year = dmy[3];
        return `${day}-${month}-${year}`;
      }
      const d = new Date(cleanStr);
      if (!isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return cleanStr;
    } catch (e) {
      return dateStr;
    }
  };

  const displayDob = formatDob(dob);
  
  // Dynamic Card Validity
  const calculatedValidity = validUntil || (sessionYear.includes('-') ? `30/06/20${sessionYear.split('-')[1]}` : '30/06/2027');

  // Stable Verification URL / Reference
  const qrRef = verificationToken 
    ? `${window.location.origin}/verify/card/${verificationToken}`
    : `VERIFY-ID:${id || 'PENDING'}:${sessionYear}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrRef)}`;

  // Card dimensions (Standard CR-80 ratio, scaled for high detail rendering)
  const CARD_W = 350;
  const CARD_H = 540;

  const cardShell = {
    width: `${CARD_W}px`,
    height: `${CARD_H}px`,
    borderRadius: '12px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: '#ffffff',
    fontFamily: "'Inter', 'Segoe UI', 'Roboto', Arial, sans-serif",
    border: '1px solid rgba(0,0,0,0.08)',
    margin: '0 auto',
    boxSizing: 'border-box',
    WebkitFontSmoothing: 'antialiased'
  };

  // Determine Class or Course display string
  const displayClassOrCourse = isSchool
    ? (className ? `${className}${section ? ' - ' + section : ''}` : '')
    : (course ? `${course}${section ? ' (' + section + ')' : ''}` : department || '');

  // Determine Teacher / Staff role
  const isTeacherRole = role === 'TEACHER' || role === 'STAFF';

  // Format clean teacher name and guarantee "Tr." prefix for Teacher ID cards
  const formatTeacherNameClean = (val) => {
    if (!val) return '';
    const trimmed = String(val).trim();
    if (!trimmed) return '';
    return trimmed
      .replace(/^tr\.?\s*/i, '')
      .replace(/^(mr|mrs|ms|miss)\.?\s+/i, '')
      .trim();
  };

  const cleanTeacherName = isTeacherRole ? formatTeacherNameClean(name) : '';

  const renderCardName = () => {
    if (isTeacherRole) {
      return (
        <span>
          <span style={{ textTransform: 'none', fontWeight: '800' }}>Tr. </span>
          <span>{cleanTeacherName || 'Teacher'}</span>
        </span>
      );
    }
    return name || 'Select Student';
  };

  // Photo Component with fallback badge when un-uploaded
  const RenderPhoto = ({ width = '110px', height = '132px', borderRadius = '8px', border = `3px solid ${primaryColor}` }) => (
    <div style={{
      width,
      height,
      borderRadius,
      border,
      overflow: 'hidden',
      backgroundColor: '#f1f5f9',
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      flexShrink: 0
    }}>
      {hasPhoto ? (
        <img src={displayPhoto} alt={name || (isTeacherRole ? 'Teacher' : 'Student')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
          textAlign: 'center',
          color: '#64748b'
        }}>
          <span style={{ fontSize: '24px', marginBottom: '4px' }}>📷</span>
          <span style={{ fontSize: '9.5px', fontWeight: '700', color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: '1.2' }}>
            Photo Not<br/>Uploaded
          </span>
        </div>
      )}
    </div>
  );

  // Reusable Field row
  const RenderField = ({ label, value, highlight = false }) => {
    if (!value) return null;
    return (
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        padding: '3.5px 0',
        borderBottom: '1px dotted #e2e8f0',
        fontSize: '11px'
      }}>
        <span style={{
          fontWeight: '700',
          color: '#475569',
          minWidth: '85px',
          textTransform: 'uppercase',
          fontSize: '9.5px',
          letterSpacing: '0.4px',
          flexShrink: 0,
          paddingTop: '1px'
        }}>{label}</span>
        <span style={{ fontWeight: '600', color: '#94a3b8', margin: '0 6px', fontSize: '10px' }}>:</span>
        <span style={{
          fontWeight: '700',
          color: highlight ? primaryColor : '#0f172a',
          fontSize: '11px',
          flex: 1,
          wordBreak: 'break-word',
          lineHeight: '1.3'
        }}>{value}</span>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  //  TEMPLATE 1: CLASSIC ACADEMIC
  // ═══════════════════════════════════════════════════
  const renderClassicFront = () => (
    <div style={cardShell} className="id-card-front" id="id-card-front-capture">
      {/* Institution Banner */}
      <div style={{
        background: primaryColor,
        padding: '12px 14px',
        textAlign: 'center',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <img src={displayLogo} alt="Logo" style={{
            width: '42px', height: '42px', objectFit: 'contain',
            borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)',
            backgroundColor: 'rgba(255,255,255,0.15)', padding: '2px', flexShrink: 0
          }} />
          <div style={{ textAlign: 'left' }}>
            {trustName && (
              <div style={{
                fontSize: '8.5px',
                fontWeight: '700',
                color: 'rgba(255,255,255,0.9)',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                marginBottom: '1px'
              }}>
                {trustName}
              </div>
            )}
            <div style={{
              fontWeight: '900', fontSize: '15px', color: '#ffffff',
              textTransform: 'uppercase', letterSpacing: '1px',
              textShadow: '0 1px 3px rgba(0,0,0,0.2)', lineHeight: '1.15'
            }}>
              {orgName}
            </div>
            {orgAddress && (
              <div style={{ fontSize: '8.5px', color: 'rgba(255,255,255,0.85)', marginTop: '2px', fontWeight: '500' }}>
                {orgAddress}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Session Strip */}
      <div style={{ background: secondaryColor, padding: '4px 0', textAlign: 'center' }}>
        <span style={{ fontWeight: '800', fontSize: '10.5px', color: '#ffffff', letterSpacing: '2px', textTransform: 'uppercase' }}>
          {role === 'STUDENT' ? 'STUDENT IDENTITY CARD' : 'TEACHER IDENTITY CARD'} • {sessionYear}
        </span>
      </div>

      {/* Photo Section */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 8px', background: '#fafbfc' }}>
        <RenderPhoto width="112px" height="135px" borderRadius="8px" border={`3px solid ${primaryColor}`} />
      </div>

      {/* Name Bar */}
      <div style={{
        background: `${primaryColor}0c`,
        borderTop: `1px solid ${primaryColor}18`,
        borderBottom: `1px solid ${primaryColor}18`,
        padding: '6px 16px',
        textAlign: 'center'
      }}>
        <div style={{
          fontWeight: '900', fontSize: '16px', color: primaryColor,
          textTransform: 'uppercase', letterSpacing: '0.8px', lineHeight: '1.2'
        }}>
          {renderCardName()}
        </div>
      </div>

      {/* Details Fields */}
      <div style={{ flex: 1, padding: '8px 18px 6px', display: 'flex', flexDirection: 'column' }}>
        {role === 'STUDENT' ? (
          <>
            <RenderField label="ROLL / ID" value={id} highlight />
            <RenderField label={isSchool ? "CLASS / SEC" : "COURSE / SEM"} value={displayClassOrCourse} />
            {fatherName && <RenderField label="FATHER / GDN" value={fatherName} />}
            {displayDob && <RenderField label="D.O.B" value={displayDob} />}
            {bloodGroup && <RenderField label="BLOOD GP" value={bloodGroup} />}
            {phone && <RenderField label="CONTACT" value={phone} />}
            {address && <RenderField label="ADDRESS" value={address} />}
          </>
        ) : (
          <>
            <RenderField label="TEACHER ID" value={id} highlight />
            <RenderField label="DEPARTMENT" value={department} />
            <RenderField label="DESIGNATION" value={designation} />
            {phone && <RenderField label="CONTACT" value={phone} />}
            {bloodGroup && <RenderField label="BLOOD GP" value={bloodGroup} />}
          </>
        )}

        {/* Bottom QR + Sign Row */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 'auto', paddingTop: '8px', paddingLeft: '4px', paddingRight: '12px', borderTop: '1px solid #e2e8f0'
        }}>
          <img src={qrCodeUrl} alt="QR" style={{
            width: '46px', height: '46px', border: '1px solid #cbd5e1',
            borderRadius: '4px', padding: '2px', backgroundColor: '#ffffff'
          }} />
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '8px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Valid Until
            </div>
            <div style={{ fontSize: '11.5px', color: primaryColor, fontWeight: '800' }}>
              {calculatedValidity}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '80px' }}>
            <div style={{ height: '26px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
              {displaySignature ? (
                <img src={displaySignature} alt="Sign" style={{ maxHeight: '24px', maxWidth: '80px', objectFit: 'contain' }} />
              ) : null}
            </div>
            <span style={{ fontSize: '7.5px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px', whiteSpace: 'nowrap' }}>
              Principal Sign
            </span>
          </div>
        </div>
      </div>

      {/* Footer Bar */}
      <div style={{ background: primaryColor, padding: '5px 14px', textAlign: 'center' }}>
        <span style={{ fontSize: '7.5px', color: 'rgba(255,255,255,0.9)', fontWeight: '500', letterSpacing: '0.3px' }}>
          {footerText}
        </span>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════
  //  TEMPLATE 2: MODERN CORPORATE
  // ═══════════════════════════════════════════════════
  const renderModernFront = () => (
    <div style={cardShell} className="id-card-front" id="id-card-front-capture">
      {/* Top Banner */}
      <div style={{ position: 'relative', height: '96px', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: '-50px', left: '-20px', right: '-20px', height: '180px',
          background: `linear-gradient(160deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
          borderRadius: '0 0 50% 50%'
        }} />
        <div style={{
          position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '10px', paddingTop: '14px'
        }}>
          <img src={displayLogo} alt="Logo" style={{
            width: '36px', height: '36px', objectFit: 'contain',
            borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)',
            backgroundColor: 'rgba(255,255,255,0.15)'
          }} />
          <div>
            {trustName && (
              <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.9)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '1px' }}>
                {trustName}
              </div>
            )}
            <div style={{ fontWeight: '800', fontSize: '14px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.8px', lineHeight: '1.2' }}>
              {orgName}
            </div>
            {orgAddress && (
              <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.82)', marginTop: '1px' }}>
                {orgAddress}
              </div>
            )}
          </div>
        </div>
        <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', marginTop: '6px' }}>
          <span style={{ fontWeight: '800', fontSize: '11px', color: secondaryColor, letterSpacing: '2px', textTransform: 'uppercase' }}>
            {role === 'STUDENT' ? 'STUDENT ID CARD' : 'TEACHER ID CARD'}
          </span>
        </div>
      </div>

      {/* Passport Photo */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px', marginBottom: '6px' }}>
        <RenderPhoto width="110px" height="132px" borderRadius="8px" border={`3px solid ${primaryColor}`} />
      </div>

      {/* Name */}
      <div style={{ textAlign: 'center', marginBottom: '4px', padding: '0 12px' }}>
        <div style={{ fontWeight: '900', fontSize: '16.5px', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {renderCardName()}
        </div>
        <div style={{ fontSize: '10.5px', color: primaryColor, fontWeight: '700', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '1px' }}>
          {role === 'STUDENT' ? displayClassOrCourse : designation}
        </div>
      </div>

      {/* Details List */}
      <div style={{
        margin: '8px 16px', padding: '10px 14px',
        background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', flex: 1
      }}>
        <RenderField label={role === 'STUDENT' ? "STUDENT ID" : "TEACHER ID"} value={id} highlight />
        {role === 'STUDENT' && <RenderField label={isSchool ? "CLASS / SEC" : "COURSE"} value={displayClassOrCourse} />}
        {fatherName && <RenderField label="FATHER" value={fatherName} />}
        {displayDob && <RenderField label="D.O.B" value={displayDob} />}
        {phone && <RenderField label="CONTACT" value={phone} />}
        {bloodGroup && <RenderField label="BLOOD GP" value={bloodGroup} />}
        {calculatedValidity && <RenderField label="VALID TILL" value={calculatedValidity} />}
      </div>

      {/* QR + Session Footer */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        padding: '0 24px 8px 18px'
      }}>
        <img src={qrCodeUrl} alt="QR" style={{
          width: '46px', height: '46px', border: '1px solid #e2e8f0',
          borderRadius: '6px', padding: '2px', backgroundColor: '#fff'
        }} />
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '85px' }}>
          <div style={{ fontSize: '8.5px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
            SESSION: {sessionYear}
          </div>
          <div style={{ height: '24px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginTop: '2px' }}>
            {displaySignature ? (
              <img src={displaySignature} alt="Sign" style={{ maxHeight: '22px', maxWidth: '80px', objectFit: 'contain' }} />
            ) : null}
          </div>
          <div style={{ fontSize: '7.5px', color: '#64748b', marginTop: '2px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
            Principal Sign
          </div>
        </div>
      </div>

      <div style={{ height: '8px', background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})` }} />
    </div>
  );

  // ═══════════════════════════════════════════════════
  //  TEMPLATE 3: MINIMALIST
  // ═══════════════════════════════════════════════════
  const renderMinimalistFront = () => (
    <div style={cardShell} className="id-card-front" id="id-card-front-capture">
      {/* Left Vertical Accent Bar */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: '10px',
        background: `linear-gradient(180deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
      }} />

      <div style={{ padding: '16px 18px 16px 24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px', marginBottom: '14px' }}>
          <div>
            {trustName && (
              <div style={{ fontSize: '8.5px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '1px' }}>
                {trustName}
              </div>
            )}
            <div style={{ fontWeight: '900', fontSize: '15px', color: primaryColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {orgName}
            </div>
            <div style={{ fontSize: '8px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1.5px', marginTop: '2px' }}>
              {role === 'STUDENT' ? 'STUDENT ID' : 'TEACHER ID'} • {sessionYear}
            </div>
          </div>
          <img src={displayLogo} alt="Logo" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
        </div>

        {/* Photo + Primary Details Side-by-Side */}
        <div style={{ display: 'flex', gap: '14px', marginBottom: '14px', alignItems: 'center' }}>
          <RenderPhoto width="100px" height="122px" borderRadius="8px" border={`2px solid ${primaryColor}`} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '900', fontSize: '16px', color: '#0f172a', textTransform: 'uppercase', lineHeight: '1.2', marginBottom: '6px' }}>
              {renderCardName()}
            </div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: primaryColor, textTransform: 'uppercase', marginBottom: '8px' }}>
              {role === 'STUDENT' ? displayClassOrCourse : designation}
            </div>
            <div style={{ background: `${primaryColor}10`, padding: '4px 8px', borderRadius: '4px', display: 'inline-block', fontSize: '10.5px', fontWeight: '800', color: primaryColor }}>
              ID: {id || '——'}
            </div>
          </div>
        </div>

        {/* Secondary Details Grid */}
        <div style={{ background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', flex: 1 }}>
          {fatherName && <RenderField label="FATHER" value={fatherName} />}
          {displayDob && <RenderField label="D.O.B" value={displayDob} />}
          {bloodGroup && <RenderField label="BLOOD GP" value={bloodGroup} />}
          {phone && <RenderField label="CONTACT" value={phone} />}
          {address && <RenderField label="ADDRESS" value={address} />}
        </div>

        {/* Footer Row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', paddingTop: '8px', paddingRight: '12px', borderTop: '1px solid #e2e8f0' }}>
          <img src={qrCodeUrl} alt="QR" style={{ width: '42px', height: '42px' }} />
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '8px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600', display: 'block' }}>Valid Until</span>
            <span style={{ fontSize: '11px', fontWeight: '800', color: primaryColor }}>{calculatedValidity}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '75px' }}>
            <div style={{ height: '24px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
              {displaySignature ? (
                <img src={displaySignature} alt="Sign" style={{ maxHeight: '22px', maxWidth: '75px', objectFit: 'contain' }} />
              ) : null}
            </div>
            <span style={{ fontSize: '7.5px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px', whiteSpace: 'nowrap' }}>
              Principal Sign
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════
  //  TEMPLATE 4: CREATIVE / PREMIUM
  // ═══════════════════════════════════════════════════
  const renderCreativeFront = () => {
    const darkBg = '#0f172a';
    return (
      <div style={{
        ...cardShell,
        backgroundColor: darkBg,
        border: `2px solid ${secondaryColor}44`
      }} className="id-card-front" id="id-card-front-capture">

        <div style={{ height: '4px', background: `linear-gradient(90deg, transparent, ${secondaryColor}, transparent)` }} />

        {/* Header */}
        <div style={{ padding: '14px 16px 10px', textAlign: 'center', background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)', borderBottom: `1px solid ${secondaryColor}25` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            <img src={displayLogo} alt="Logo" style={{
              width: '40px', height: '40px', objectFit: 'contain',
              borderRadius: '50%', border: `2px solid ${secondaryColor}66`,
              backgroundColor: 'rgba(255,255,255,0.05)', padding: '2px', flexShrink: 0
            }} />
            <div style={{ textAlign: 'left' }}>
              {trustName && (
                <div style={{ fontSize: '8.5px', color: secondaryColor, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '1px' }}>
                  {trustName}
                </div>
              )}
              <div style={{ fontWeight: '900', fontSize: '15px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1.2px', lineHeight: '1.15' }}>
                {orgName}
              </div>
              {orgAddress && (
                <div style={{ fontSize: '8px', color: secondaryColor, marginTop: '2px', fontWeight: '500', opacity: 0.9 }}>
                  {orgAddress}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Type Badge */}
        <div style={{ background: secondaryColor, padding: '4px 0', textAlign: 'center' }}>
          <span style={{ fontWeight: '900', fontSize: '10px', color: '#0f172a', letterSpacing: '2.5px', textTransform: 'uppercase' }}>
            ★ {role === 'STUDENT' ? 'STUDENT IDENTITY CARD' : 'TEACHER IDENTITY CARD'} ★
          </span>
        </div>

        {/* Photo */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 8px' }}>
          <RenderPhoto width="112px" height="135px" borderRadius="10px" border={`3px solid ${secondaryColor}`} />
        </div>

        {/* Name */}
        <div style={{ textAlign: 'center', padding: '0 16px', marginBottom: '8px' }}>
          <div style={{ fontWeight: '900', fontSize: '17px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: '1.2' }}>
            {renderCardName()}
          </div>
          <div style={{ fontSize: '9.5px', color: secondaryColor, fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px', marginTop: '2px' }}>
            {role === 'STUDENT' ? displayClassOrCourse : designation}
          </div>
        </div>

        {/* Details Card */}
        <div style={{
          margin: '0 16px', padding: '10px 14px',
          background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: `1px solid ${secondaryColor}25`,
          flex: 1, display: 'flex', flexDirection: 'column'
        }}>
          {[
            { label: role === 'STUDENT' ? 'STUDENT ID' : 'TEACHER ID', value: id },
            fatherName && { label: "FATHER'S NAME", value: fatherName },
            displayDob && { label: 'DATE OF BIRTH', value: displayDob },
            phone && { label: 'CONTACT PHONE', value: phone },
            bloodGroup && { label: 'BLOOD GROUP', value: bloodGroup },
            { label: 'VALID UNTIL', value: calculatedValidity }
          ].filter(Boolean).map((row, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4.5px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <span style={{ fontSize: '9px', fontWeight: '700', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>{row.label}</span>
              <span style={{ fontSize: '10.5px', fontWeight: '800', color: '#ffffff' }}>{row.value}</span>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '10px 22px 8px 16px' }}>
          <img src={qrCodeUrl} alt="QR" style={{ width: '46px', height: '46px', borderRadius: '6px', padding: '2px', backgroundColor: '#fff' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '8px', color: secondaryColor, fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase' }}>
              SESSION: {sessionYear}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '75px' }}>
            <div style={{ height: '24px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
              {displaySignature ? (
                <img src={displaySignature} alt="Sign" style={{ maxHeight: '22px', maxWidth: '75px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
              ) : null}
            </div>
            <span style={{ fontSize: '7.5px', color: 'rgba(255,255,255,0.7)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px', whiteSpace: 'nowrap' }}>
              Principal Sign
            </span>
          </div>
        </div>

        <div style={{ height: '4px', background: `linear-gradient(90deg, transparent, ${secondaryColor}, transparent)` }} />
      </div>
    );
  };

  // ═══════════════════════════════════════════════════
  //  BACK SIDE (Shared Across Templates)
  // ═══════════════════════════════════════════════════
  const renderBack = () => {
    const isCreative = templateName === 'Creative' || templateName === 'Premium';
    const darkBg = '#0f172a';

    return (
      <div style={{
        ...cardShell,
        backgroundColor: isCreative ? darkBg : '#ffffff'
      }} className="id-card-back" id="id-card-back-capture">
        
        <div style={{
          height: isCreative ? '4px' : '8px',
          background: isCreative
            ? `linear-gradient(90deg, transparent, ${secondaryColor}, transparent)`
            : `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`
        }} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '14px 16px', justifyContent: 'space-between' }}>
          {/* Header */}
          <div style={{
            textAlign: 'center', marginBottom: '10px', paddingBottom: '8px',
            borderBottom: isCreative ? `1px solid ${secondaryColor}25` : `2px solid ${primaryColor}20`
          }}>
            <div style={{
              fontWeight: '900', fontSize: '14px',
              color: isCreative ? '#ffffff' : primaryColor,
              textTransform: 'uppercase', letterSpacing: '0.8px'
            }}>
              {orgName}
            </div>
            <div style={{
              fontSize: '9.5px', color: isCreative ? 'rgba(255,255,255,0.85)' : '#0f172a',
              fontWeight: '600', marginTop: '3px', textTransform: 'uppercase', letterSpacing: '0.3px'
            }}>
              {orgAddress || footerText}
            </div>
          </div>

          {/* Terms & Conditions */}
          <div style={{ marginBottom: '10px' }}>
            <h4 style={{
              margin: '0 0 5px 0', fontSize: '11px', fontWeight: '800',
              color: isCreative ? secondaryColor : primaryColor,
              textTransform: 'uppercase', letterSpacing: '0.8px'
            }}>
              Terms & Conditions
            </h4>
            <ol style={{
              margin: 0, paddingLeft: '16px', fontSize: '9.5px',
              color: isCreative ? 'rgba(255,255,255,0.95)' : '#0f172a',
              lineHeight: '1.45', fontWeight: '600'
            }}>
              <li style={{ marginBottom: '3px' }}>This ID card is non-transferable and remains property of the institution.</li>
              <li style={{ marginBottom: '3px' }}>Loss of card must be reported immediately to school administration.</li>
              <li style={{ marginBottom: '3px' }}>Must be presented upon request by authorized personnel.</li>
              <li style={{ marginBottom: '3px' }}>Valid only for the active academic session ({sessionYear}).</li>
            </ol>
          </div>

          {/* Residential Address & Emergency Contact */}
          <div style={{
            background: isCreative ? 'rgba(255,255,255,0.05)' : '#f8fafc',
            borderRadius: '8px', padding: '8px 11px',
            border: isCreative ? `1px solid ${secondaryColor}25` : '1px solid #cbd5e1',
            marginBottom: '9px'
          }}>
            <div style={{
              fontSize: '10px', fontWeight: '800',
              color: isCreative ? secondaryColor : primaryColor,
              marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px'
            }}>
              Residential Address
            </div>
            <div style={{
              fontSize: '10px',
              color: isCreative ? '#ffffff' : '#0f172a',
              lineHeight: '1.4', fontWeight: '600', marginBottom: '5px'
            }}>
              {address || 'Address not provided in records'}
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderTop: isCreative ? `1px solid ${secondaryColor}25` : '1px solid #cbd5e1',
              paddingTop: '4px', fontSize: '9.5px'
            }}>
              <span style={{ fontWeight: '700', color: isCreative ? 'rgba(255,255,255,0.9)' : '#0f172a' }}>Emergency Contact:</span>
              <span style={{ fontWeight: '900', fontSize: '10.5px', color: isCreative ? secondaryColor : primaryColor }}>
                {emergencyContact || phone || 'Not Specified'}
              </span>
            </div>
          </div>

          {/* School Helpdesk & Administration Info */}
          <div style={{
            background: isCreative ? 'rgba(255,255,255,0.05)' : '#f8fafc',
            borderRadius: '8px', padding: '8px 11px',
            border: isCreative ? `1px solid ${secondaryColor}25` : '1px solid #cbd5e1',
            marginBottom: '9px'
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '4px', borderBottom: isCreative ? `1px solid ${secondaryColor}20` : '1px solid #cbd5e1',
              paddingBottom: '3px'
            }}>
              <span style={{
                fontSize: '9.5px', fontWeight: '800',
                color: isCreative ? secondaryColor : primaryColor,
                textTransform: 'uppercase', letterSpacing: '0.5px'
              }}>
                School Helpdesk & Administration
              </span>
              <span style={{ fontSize: '8.5px', color: isCreative ? 'rgba(255,255,255,0.9)' : '#0f172a', fontWeight: '700' }}>
                Hours: 8:30 AM - 4:30 PM
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '8.5px', marginBottom: '5px' }}>
              <div>
                <span style={{ color: isCreative ? 'rgba(255,255,255,0.75)' : '#334155', fontWeight: '700', display: 'block', fontSize: '8px', textTransform: 'uppercase' }}>Helpdesk Phone</span>
                <span style={{ fontWeight: '800', fontSize: '9.5px', color: isCreative ? '#fff' : '#0f172a' }}>
                  {orgPhone || 'Contact Administration'}
                </span>
              </div>
              <div>
                <span style={{ color: isCreative ? 'rgba(255,255,255,0.75)' : '#334155', fontWeight: '700', display: 'block', fontSize: '8px', textTransform: 'uppercase' }}>Email Support</span>
                <span style={{ fontWeight: '800', fontSize: '9.5px', color: isCreative ? '#fff' : '#0f172a', wordBreak: 'break-all' }}>
                  {orgEmail || 'office@school.edu'}
                </span>
              </div>
            </div>

            <div style={{
              paddingTop: '4px',
              borderTop: isCreative ? `1px dashed ${secondaryColor}30` : '1px dashed #94a3b8',
              fontSize: '8.5px', color: isCreative ? 'rgba(255,255,255,0.95)' : '#0f172a',
              lineHeight: '1.35', fontWeight: '500'
            }}>
              <strong style={{ fontWeight: '800', color: isCreative ? secondaryColor : primaryColor }}>Return Instructions: </strong>
              If found, please drop into any post box or return to the school administrative reception office.
            </div>
          </div>

          {/* Barcode Strip */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', margin: '3px 0 5px'
          }}>
            <div style={{ display: 'flex', gap: '1.5px', alignItems: 'center', height: '20px' }}>
              {[2, 1, 3, 1, 2, 1, 3, 2, 1, 2, 3, 1, 1, 2, 3, 2, 1, 3, 1, 2, 1, 3, 2, 1, 3, 2, 1, 1, 3, 2, 1, 2, 3, 1, 2].map((w, i) => (
                <div key={i} style={{
                  width: `${w}px`,
                  height: i % 4 === 0 ? '20px' : '16px',
                  backgroundColor: isCreative ? secondaryColor : '#0f172a'
                }} />
              ))}
            </div>
            <div style={{
              fontSize: '9px', color: isCreative ? 'rgba(255,255,255,0.85)' : '#0f172a',
              fontWeight: '800', letterSpacing: '2px', marginTop: '2px', textTransform: 'uppercase'
            }}>
              * {id || 'SCH-ID'} *
            </div>
          </div>

          {/* Seal & Signature Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 'auto', paddingTop: '4px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '45%' }}>
              <div style={{ height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {displaySeal ? (
                  <img src={displaySeal} alt="Seal" style={{
                    maxHeight: '44px', maxWidth: '70px', objectFit: 'contain',
                    filter: isCreative ? 'brightness(0) invert(1) opacity(0.7)' : 'none'
                  }} />
                ) : null}
              </div>
              <span style={{
                fontSize: '8.5px', color: isCreative ? 'rgba(255,255,255,0.85)' : '#0f172a',
                marginTop: '4px', textTransform: 'uppercase', fontWeight: '800', letterSpacing: '0.5px'
              }}>
                Institution Seal
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '45%' }}>
              <div style={{ height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {displaySignature ? (
                  <img src={displaySignature} alt="Signature" style={{
                    maxHeight: '36px', maxWidth: '90px', objectFit: 'contain',
                    filter: isCreative ? 'brightness(0) invert(1) opacity(0.8)' : 'none'
                  }} />
                ) : null}
              </div>
              <span style={{
                fontSize: '8.5px', color: isCreative ? 'rgba(255,255,255,0.85)' : '#0f172a',
                marginTop: '4px', textTransform: 'uppercase', fontWeight: '800', letterSpacing: '0.5px'
              }}>
                Authorized Sign
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ background: isCreative ? 'transparent' : primaryColor, padding: isCreative ? '0' : '6px 16px', textAlign: 'center' }}>
          {isCreative ? (
            <div style={{ height: '4px', background: `linear-gradient(90deg, transparent, ${secondaryColor}, transparent)` }} />
          ) : (
            <div style={{ fontSize: '8.5px', color: '#ffffff', fontWeight: '700', letterSpacing: '0.4px' }}>
              {footerText}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Dispatch Front Side Template
  const renderFront = () => {
    switch (templateName) {
      case 'Modern Corporate':
        return renderModernFront();
      case 'Minimalist':
        return renderMinimalistFront();
      case 'Creative':
      case 'Premium':
        return renderCreativeFront();
      case 'Classic Academic':
      default:
        return renderClassicFront();
    }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'center' }}>
      {(side === 'front' || side === 'both') && renderFront()}
      {(side === 'back' || side === 'both') && renderBack()}
    </div>
  );
};

export default DocumentRenderer;
